var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { computed, observable } from "mobx";
import { NamedRoutes } from "./NamedRoutes";
import { isErr } from "healthex-common/result";
import { NOT_LOADED } from "../Infra/RemoteData";
import { AuthType } from "healthex-common/auth.dtos";
import { AccountStatus, UserType } from "healthex-common/user.dtos";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("common");
export class WelcomeUserViewModel {
    constructor(container) {
        this.input = { password: "", reEnteredPassword: "" };
        this.error = { password: null, reEnteredPassword: null, message: null };
        this.welcomeUserUIManager = container.welcomeUserUIManager;
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
        this.passwordValidator = container.passwordValidator;
    }
    get user() {
        return this.welcomeUserUIManager.authenticatedUser;
    }
    get organization() {
        return this.welcomeUserUIManager.organization;
    }
    get authType() {
        return this.welcomeUserUIManager.authTypeResponse;
    }
    get organizationName() {
        var _a;
        if (this.organization === NOT_LOADED)
            return null;
        return (_a = this.organization) === null || _a === void 0 ? void 0 : _a.name;
    }
    get organizationPasswordConfiguration() {
        var _a;
        if (this.organization === NOT_LOADED)
            return null;
        return (_a = this.organization) === null || _a === void 0 ? void 0 : _a.passwordConfiguration;
    }
    get userEmail() {
        if (this.user === NOT_LOADED)
            return null;
        if (this.user && this.user.type !== UserType.PATIENT && this.user.type !== UserType.SCREENSHOT_USER) {
            return this.user.email;
        }
        return null;
    }
    get invalidInvite() {
        var _a;
        if (this.user === NOT_LOADED)
            return false;
        if (this.organization === NOT_LOADED)
            return false;
        if (this.authType === NOT_LOADED)
            return false;
        return this.organization === null || (this.user === null && ((_a = this.authType) === null || _a === void 0 ? void 0 : _a.authType) !== AuthType.SSO_SAML);
    }
    get showHealthcareOrgInvalidInviteImage() {
        return this.isHealthcareOrg && this.invalidInvite;
    }
    get showResearcherInvalidInviteImage() {
        return !this.isHealthcareOrg && this.invalidInvite;
    }
    get welcomeLinkAccepted() {
        if (this.user === NOT_LOADED)
            return false;
        if (this.user && this.user.type !== UserType.PATIENT && this.user.type !== UserType.SCREENSHOT_USER) {
            return this.user.accountStatus === AccountStatus.ACTIVE;
        }
        return false;
    }
    get isAuthTypePassword() {
        if (this.authType === NOT_LOADED)
            return null;
        return this.authType && this.authType.authType === AuthType.USERNAME_PASSWORD;
    }
    get isAuthTypeSAML() {
        if (this.authType === NOT_LOADED)
            return null;
        return this.authType && this.authType.authType === AuthType.SSO_SAML;
    }
    get isDisabled() {
        return !this.input.password || !this.input.reEnteredPassword;
    }
    get isHealthcareOrg() {
        return this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION;
    }
    get ssoProviderName() {
        var _a;
        if (this.authType === NOT_LOADED)
            return null;
        return (_a = this.authType) === null || _a === void 0 ? void 0 : _a.provider;
    }
    async navigateToLoginPage() {
        this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_ORG_USER);
    }
    continueWithSSO() {
        var _a;
        if (this.authType === NOT_LOADED)
            return;
        if (this.authType && this.authType.authType === AuthType.SSO_SAML) {
            const redirectURL = (_a = this.authType) === null || _a === void 0 ? void 0 : _a.redirectUrl;
            if (redirectURL) {
                window.location.href = redirectURL;
            }
        }
    }
    updateInput(update) {
        this.error = { password: null, reEnteredPassword: null, message: null };
        this.input = { ...this.input, ...update };
    }
    updateError(update) {
        this.error = { ...this.error, ...update };
    }
    async updatePassword() {
        const validationResult = this.passwordValidator.validate(this.input.password, this.input.reEnteredPassword, null, null, null, this.organizationPasswordConfiguration);
        if (isErr(validationResult)) {
            this.getValidationErrorMessage(validationResult.error);
            return;
        }
        if (this.welcomeUserUIManager.welcomeToken) {
            try {
                await this.authManager.resetPassword(this.input.password, this.welcomeUserUIManager.welcomeToken);
                if (this.userEmail) {
                    const result = await this.authManager.authenticateByPassword(this.userEmail, this.input.password);
                    if (!isErr(result)) {
                        this.routingUIManager.navigateTo(NamedRoutes.WELCOME_ORG_USER_DETAILS);
                    }
                }
            }
            catch (e) {
                if (e instanceof Error) {
                    this.updateError({ message: e.message });
                }
                else {
                    this.updateError({ message: t("An error occurred") });
                }
            }
        }
    }
    getValidationErrorMessage(validationError) {
        switch (validationError) {
            case "OLD_PASSWORD_REUSED":
                this.updateError({ password: t("Old password cannot be reused. Please use a new password") });
                break;
            case "PASSWORD_MISMATCH":
                this.updateError({ reEnteredPassword: t("Passwords don't match! Please try again.") });
                break;
            case "PASSWORD_CRITERIA_MISMATCH":
                this.updateError({ password: t("Password doesn't meet the requirements! Please try again.") });
                break;
            case "SSO_ENABLED":
                this.updateError({ password: t("Password cannot be changed when SSO is enabled.") });
                break;
            default:
                this.updateError({ message: t("An unexpected error occurred") });
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], WelcomeUserViewModel.prototype, "input", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WelcomeUserViewModel.prototype, "error", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "user", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "organization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "authType", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "organizationName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "organizationPasswordConfiguration", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "userEmail", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "invalidInvite", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "showHealthcareOrgInvalidInviteImage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "showResearcherInvalidInviteImage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "welcomeLinkAccepted", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "isAuthTypePassword", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "isAuthTypeSAML", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "isDisabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "isHealthcareOrg", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], WelcomeUserViewModel.prototype, "ssoProviderName", null);
